import { useCallback, useEffect, useRef, useState } from "react";

const mappableEntities = (items) => (items || []).filter((entity) => entity.mappable !== false);

const useMappingContext = ({
    initialConfigSet,
    initialEntityName,
    initialFileType,
    onContextReset,
    onFetchEntities,
    onFetchMappingContexts,
    projectId,
}) => {
    const [contexts, setContexts] = useState([]);
    const [countryCode, setCountryCode] = useState("");
    const [legalStructureId, setLegalStructureId] = useState("");
    const [configSet, setConfigSet] = useState("");
    const [entityName, setEntityName] = useState("");
    const [fileType, setFileType] = useState("");
    const [entities, setEntities] = useState([]);
    const [loadingContexts, setLoadingContexts] = useState(true);
    const [loadingEntities, setLoadingEntities] = useState(false);
    const [contextError, setContextError] = useState(false);
    const [entityError, setEntityError] = useState(false);
    const [unresolved, setUnresolved] = useState(false);
    const contextRequestId = useRef(0);
    const requestId = useRef(0);

    const selectedContext = contexts.find((context) => context.country_code === countryCode);
    const selectedStructure = selectedContext?.legal_structures?.find(
        (structure) => structure.id === legalStructureId,
    );

    const resetEntity = useCallback(() => {
        setEntityName("");
        setFileType("");
        setEntities([]);
        setEntityError(false);
        setLoadingEntities(false);
        setUnresolved(false);
        onContextReset();
    }, [onContextReset]);

    const fetchEntities = useCallback(
        async (structure) => {
            const currentRequest = ++requestId.current;
            setLoadingEntities(true);
            setEntityError(false);
            try {
                const result = await onFetchEntities(structure.config_set, structure.id);
                if (!Array.isArray(result)) throw new Error("Invalid entity response");
                if (currentRequest !== requestId.current) return;
                setEntities(mappableEntities(result));
            } catch (error) {
                if (currentRequest === requestId.current) {
                    setEntities([]);
                    setEntityError(true);
                }
            } finally {
                if (currentRequest === requestId.current) setLoadingEntities(false);
            }
        },
        [onFetchEntities],
    );

    const selectStructure = useCallback(
        (structure) => {
            requestId.current += 1;
            resetEntity();
            setLegalStructureId(structure?.id || "");
            setConfigSet(structure?.config_set || "");
            if (!structure || structure.mappable_entity_count === 0) return;
            fetchEntities(structure);
        },
        [fetchEntities, resetEntity],
    );

    const selectCountry = useCallback(
        (value) => {
            requestId.current += 1;
            resetEntity();
            setCountryCode(value);
            setLegalStructureId("");
            setConfigSet("");
            const context = contexts.find((item) => item.country_code === value);
            if (context?.legal_structures?.length === 1) {
                selectStructure(context.legal_structures[0]);
            }
        },
        [contexts, resetEntity, selectStructure],
    );

    const selectEntity = useCallback(
        (value) => {
            const match = entities.find((entity) => entity.file_type === value);
            setEntityName(match?.entity_name || "");
            setFileType(match?.file_type || "");
        },
        [entities],
    );

    const restoreMapping = useCallback(
        async (items, currentContextRequest) => {
            if (!initialConfigSet || (!initialFileType && !initialEntityName)) return;
            const candidates = items.flatMap((context) =>
                (context.legal_structures || [])
                    .filter((structure) => structure.config_set === initialConfigSet)
                    .map((structure) => ({ context, structure })),
            );
            for (const candidate of candidates) {
                try {
                    const result = await onFetchEntities(
                        candidate.structure.config_set,
                        candidate.structure.id,
                    );
                    if (currentContextRequest !== contextRequestId.current) return;
                    if (!Array.isArray(result)) continue;
                    const match =
                        result.find((entity) => entity.file_type === initialFileType) ||
                        result.find((entity) => entity.entity_name === initialEntityName);
                    if (match?.mappable !== false) {
                        setCountryCode(candidate.context.country_code);
                        setLegalStructureId(candidate.structure.id);
                        setConfigSet(candidate.structure.config_set);
                        setEntities(mappableEntities(result));
                        setEntityName(match.entity_name);
                        setFileType(match.file_type);
                        setUnresolved(false);
                        return;
                    }
                } catch (error) {
                    if (currentContextRequest !== contextRequestId.current) return;
                    setEntityError(true);
                }
            }
            if (currentContextRequest !== contextRequestId.current) return;
            setEntityName(initialEntityName || "");
            setFileType("");
            setUnresolved(true);
        },
        [initialConfigSet, initialEntityName, initialFileType, onFetchEntities],
    );

    const loadContexts = useCallback(async () => {
        const currentContextRequest = ++contextRequestId.current;
        requestId.current += 1;
        setLoadingContexts(true);
        setContextError(false);
        setUnresolved(false);
        setContexts([]);
        setCountryCode("");
        setLegalStructureId("");
        setConfigSet("");
        resetEntity();
        try {
            const result = await onFetchMappingContexts(projectId);
            if (currentContextRequest !== contextRequestId.current) return;
            if (!Array.isArray(result)) throw new Error("Invalid context response");
            setContexts(result);
            await restoreMapping(result, currentContextRequest);
        } catch (error) {
            if (currentContextRequest !== contextRequestId.current) return;
            setContexts([]);
            setContextError(true);
        } finally {
            if (currentContextRequest === contextRequestId.current) {
                setLoadingContexts(false);
            }
        }
    }, [onFetchMappingContexts, projectId, resetEntity, restoreMapping]);

    useEffect(() => {
        loadContexts();
    }, [loadContexts]);

    return {
        configSet,
        contextError,
        contexts,
        countryCode,
        entities,
        entityError,
        entityName,
        hasNoContexts: !loadingContexts && !contextError && contexts.length === 0,
        fileType,
        legalStructureId,
        loadingContexts,
        loadingEntities,
        loadContexts,
        retryEntities: () => selectedStructure && fetchEntities(selectedStructure),
        selectCountry,
        selectEntity,
        selectStructure: (value) =>
            selectStructure(
                selectedContext?.legal_structures?.find((structure) => structure.id === value),
            ),
        selectedStructure,
        unresolved,
    };
};

export default useMappingContext;
